import { computed, unref, watch } from 'vue';
import { AxiosPromise, AxiosError, AxiosResponse } from '@libs/http';
import { useQuery, UseQueryOptions } from '@tanstack/vue-query';
import { MaybeRef, useCloned } from '@vueuse/core';

import { useCallback } from './useCallback';

export type UseQueryConfigV2<T> = UseQueryOptions<
  AxiosPromise<T>,
  AxiosError,
  AxiosResponse<T>,
  any[]
> & {
  key: any[];
  immediate?: MaybeRef<boolean> | ((...arg: any[]) => boolean);
  onReject?: (...arg: any[]) => void;
  onSettled?: (...arg: any[]) => void;
  onSuccess?: (...arg: any[]) => void;
};

export const queryStatus = {
  loading: 'loading',
  error: 'error',
  success: 'success',
} as const;

function getReactiveValues(key: any[]): string[] {
  return key.filter((item) => unref(item) !== item);
}

export function useQueryV2<T>(
  fetcher: (...arg: any[]) => AxiosPromise<T>,
  {
    key = [],
    staleTime = 1, // 1s
    cacheTime = 1000 * 60 * 60, // 1h
    refetchOnWindowFocus = false,

    immediate = true,
    refetchInterval = false,

    onReject = () => {},
    onSuccess = () => {},
    onSettled = () => {},
  }: UseQueryConfigV2<T>
) {
  const onKeyUpdated = useCallback();

  const reactiveValuesInKey = getReactiveValues(key);
  const shouldFetch = computed(() =>
    typeof immediate === 'function' ? immediate() : unref(immediate)
  );

  const {
    data,
    refetch,
    isError,
    isLoading,
    isFetching,
    isSuccess,
    fetchStatus,
    status,

    ...query
  } = useQuery({
    queryFn: fetcher,
    queryKey: key,
    cacheTime,
    staleTime,
    onSuccess,
    onError: onReject,
    enabled: shouldFetch,
    refetchOnWindowFocus,
    refetchOnReconnect: true,
    keepPreviousData: true,
    retry: 1,
    retryDelay: 1000,
    refetchInterval,
  });

  const result = computed(() => {
    if (data.value) {
      const { cloned } = useCloned<AxiosResponse<T>>(data?.value);

      return cloned.value;
    }

    return undefined;
  });

  if (isFetching.value) {
    onSettled?.();
  }

  function execute() {
    onSettled?.();
    return refetch();
  }

  watch(reactiveValuesInKey, (_, [oldKey]) => {
    onKeyUpdated.trigger({ oldKey });
  });

  return {
    execute,
    onKeyUpdated: onKeyUpdated.on,

    isIdle: computed(() => fetchStatus.value === 'idle'),
    isPending: isLoading,
    isRevalidating: isFetching,
    isError: isError,
    isSuccess: isSuccess,

    status,
    result,
    ...query,
  };
}
